import { useEffect, useState, useCallback } from "react";
import DoctorLayout from "@/pages/doctor/DoctorLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { UserPlus, KeyRound, Pencil, Trash2, RotateCcw, UserCog } from "lucide-react";
import { formatDate } from "@/lib/date";
import { validateEmail, normalizeEmail, validatePassword, validateConfirmPassword } from "@/lib/validation";
import CreateStaffDialog from "@/components/staff-management/CreateStaffDialog";
import EditStaffDialog from "@/components/staff-management/EditStaffDialog";
import ResetStaffPasswordDialog from "@/components/staff-management/ResetStaffPasswordDialog";
import DeleteStaffDialog from "@/components/staff-management/DeleteStaffDialog";

function initials(name) {
  if (!name) return "";
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function StaffManagementPage() {
  const [staff, setStaff] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", temp_password: "", temp_password_confirm: "" });
  const [createTouched, setCreateTouched] = useState({});
  const [createEmailStatus, setCreateEmailStatus] = useState(null);
  const [editName, setEditName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [resetTouched, setResetTouched] = useState({});
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => api.get("/doctor/staff").then(({ data }) => setStaff(data)), []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!createOpen || validateEmail(form.email)) {
      setCreateEmailStatus(null);
      return;
    }
    const t = setTimeout(() => {
      api.get("/doctor/staff/check-email", { params: { email: normalizeEmail(form.email) } }).then(({ data }) => setCreateEmailStatus(data));
    }, 400);
    return () => clearTimeout(t);
  }, [form.email, createOpen]);

  const createFieldErrors = {
    name: !form.name.trim() ? "Name is required" : null,
    email: validateEmail(form.email) || (createEmailStatus && !createEmailStatus.available ? "This email is already registered" : null),
    temp_password: validatePassword(form.temp_password),
    temp_password_confirm: validateConfirmPassword(form.temp_password, form.temp_password_confirm),
  };
  const createIsValid = Object.values(createFieldErrors).every((e) => !e);

  const createStaff = async () => {
    setCreateTouched({ name: true, email: true, temp_password: true, temp_password_confirm: true });
    if (!createIsValid) return;
    try {
      await api.post("/doctor/staff", { name: form.name, email: normalizeEmail(form.email), temp_password: form.temp_password });
      toast.success("Receptionist account created");
      setCreateOpen(false);
      setForm({ name: "", email: "", temp_password: "", temp_password_confirm: "" });
      setCreateTouched({});
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openEdit = (s) => {
    setSelectedStaff(s);
    setEditName(s.name);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/doctor/staff/${selectedStaff.id}`, { name: editName });
      toast.success("Staff details updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reactivate = async (s) => {
    try {
      await api.put(`/doctor/staff/${s.id}`, { is_active: true });
      toast.success(`${s.name} reactivated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/doctor/staff/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name}'s account has been deactivated`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const resetFieldErrors = {
    newPassword: validatePassword(newPassword),
    newPasswordConfirm: validateConfirmPassword(newPassword, newPasswordConfirm),
  };
  const resetIsValid = Object.values(resetFieldErrors).every((e) => !e);

  const resetPassword = async () => {
    setResetTouched({ newPassword: true, newPasswordConfirm: true });
    if (!resetIsValid) return;
    try {
      await api.post(`/doctor/staff/${selectedStaff.id}/reset-password`, { new_password: newPassword });
      toast.success("Staff password reset. They will be asked to change it on next login.");
      setResetOpen(false);
      setNewPassword("");
      setNewPasswordConfirm("");
      setResetTouched({});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <DoctorLayout>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900" data-testid="staff-management-heading">Staff Management</h1>
        <Button data-testid="add-staff-btn" className="gap-2 bg-teal-700 hover:bg-teal-800" onClick={() => setCreateOpen(true)}>
          <UserPlus className="h-4 w-4" /> Add Receptionist
        </Button>
      </div>

      <div className="mt-6">
        <Table>
          <TableHeader>
            <TableRow className="sticky top-0">
              <TableHead className="py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Staff</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Created</TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {staff.map((s, i) => (
              <TableRow
                key={s.id}
                className="group transition-colors hover:bg-teal-50/40"
                data-testid={`staff-row-${s.email}`}
              >
                <TableCell className="py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-50 text-xs font-semibold text-teal-700">
                      {initials(s.name) || <UserCog className="h-4 w-4" />}
                    </span>
                    <span className="font-medium text-slate-900">{s.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-slate-600">{s.email}</TableCell>
                <TableCell>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${s.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                    {s.is_active ? "Active" : "Deactivated"}
                  </span>
                </TableCell>
                <TableCell className="text-slate-600">{formatDate(s.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <button
                      data-testid={`edit-staff-${s.email}`}
                      title="Edit Staff"
                      onClick={() => openEdit(s)}
                      className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:border-teal-700 hover:bg-teal-50 hover:text-teal-700"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`reset-password-${s.email}`}
                      title="Reset Password"
                      onClick={() => { setSelectedStaff(s); setResetOpen(true); }}
                      className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:border-teal-700 hover:bg-teal-50 hover:text-teal-700"
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                    </button>
                    {s.is_active ? (
                      <button
                        data-testid={`delete-staff-${s.email}`}
                        title="Delete Staff"
                        onClick={() => setDeleteTarget(s)}
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:border-red-600 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button
                        data-testid={`reactivate-staff-${s.email}`}
                        title="Reactivate Staff"
                        onClick={() => reactivate(s)}
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:border-green-600 hover:bg-green-50 hover:text-green-600"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {staff.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-400">No staff accounts yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <CreateStaffDialog
        open={createOpen}
        onOpenChange={(o) => { setCreateOpen(o); if (!o) { setForm({ name: "", email: "", temp_password: "", temp_password_confirm: "" }); setCreateTouched({}); } }}
        form={form}
        setForm={setForm}
        createTouched={createTouched}
        setCreateTouched={setCreateTouched}
        createFieldErrors={createFieldErrors}
        createIsValid={createIsValid}
        createEmailStatus={createEmailStatus}
        onCreate={createStaff}
      />

      <EditStaffDialog open={editOpen} onOpenChange={setEditOpen} selectedStaff={selectedStaff} editName={editName} setEditName={setEditName} onSave={saveEdit} />

      <ResetStaffPasswordDialog
        open={resetOpen}
        onOpenChange={(o) => { setResetOpen(o); if (!o) { setNewPassword(""); setNewPasswordConfirm(""); setResetTouched({}); } }}
        selectedStaff={selectedStaff}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        newPasswordConfirm={newPasswordConfirm}
        setNewPasswordConfirm={setNewPasswordConfirm}
        resetTouched={resetTouched}
        setResetTouched={setResetTouched}
        resetFieldErrors={resetFieldErrors}
        resetIsValid={resetIsValid}
        onSubmit={resetPassword}
      />

      <DeleteStaffDialog target={deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)} deleting={deleting} onConfirm={confirmDelete} />
    </DoctorLayout>
  );
}
